import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import SendReferralDialog from 'src/features/business/referrals/components/send-referral/send-referral-dialog'
import { useSendReferralStore } from 'src/features/business/referrals/stores/send-referral-store'
import { UserChatButton } from 'src/features/chats/components/buttons/user-chat-button'
import { sendEvent } from 'src/shared/api/events/send-event'
import { VerificationBadge } from 'src/shared/components/icons/verification-badge'
import { Button } from 'src/shared/components/ui/buttons/button'
import { FixedFooter } from 'src/shared/components/ui/miscellaneous/fixed-footer'
import TruncatedText from 'src/shared/components/ui/miscellaneous/truncated-text'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { useUserByUsername } from 'src/shared/features/users/api/accounts/use-user-by-username'
import { usePublicLicenses } from 'src/shared/features/users/api/licenses/use-public-licenses'
import { useUserReviews } from 'src/shared/features/users/api/marketplace/use-user-reviews'
import { ExpertiseIcon } from 'src/shared/features/users/assets/general-info/expertise'
import { LanguagesIcon } from 'src/shared/features/users/assets/general-info/languages'
import { VerifiedLicensesIcon } from 'src/shared/features/users/assets/general-info/licenses'
import { WorksAtIcon } from 'src/shared/features/users/assets/general-info/works-at'
import CoverPhoto from 'src/shared/features/users/components/profile-photos/cover-photo'
import { ProfileAvatar } from 'src/shared/features/users/components/profile-photos/profile-avatar'
import BioModal from 'src/shared/features/users/components/public-profile/bio/bio-modal'
import UserFeedList from 'src/shared/features/users/components/public-profile/feed/user-feed-list'
import GeneralInfoSection from 'src/shared/features/users/components/public-profile/general-info-section'
import ListingList from 'src/shared/features/users/components/public-profile/listings/listing-list'
import OtherInfoContainer from 'src/shared/features/users/components/public-profile/other-info-container'
import PortfolioList from 'src/shared/features/users/components/public-profile/portfolio/portfolio-list'
import ReviewList from 'src/shared/features/users/components/public-profile/reviews/review-list'
import { expertiseMap } from 'src/shared/features/users/schemas/expertise'
import type { Expertise } from 'src/shared/features/users/schemas/expertise'
import type { Language } from 'src/shared/features/users/schemas/language'
import { languageMap } from 'src/shared/features/users/schemas/language'
import type { License, State } from 'src/shared/features/users/schemas/licenses'
import type { Profession } from 'src/shared/features/users/schemas/profession'
import { professionMap } from 'src/shared/features/users/schemas/profession'
import { statesMap } from 'src/shared/features/users/schemas/states'
import type { PublicUserData } from 'src/shared/features/users/schemas/user'
import VisibilityBadge from 'src/shared/features/visibility/components/badge'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { useEventStore } from 'src/shared/store/event-store'
import { cn } from 'src/shared/utils/cn'

type ActiveTab = 'listings' | 'reviews' | 'portfolio' | 'activity'

const Tab = ({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) => {
  return (
    <div
      className={cn(
        'flex flex-1 cursor-pointer items-center justify-center bg-white pb-[14px] pt-1 transition-all duration-200 ease-in-out sm:pt-2',
        active && 'border-b-primary',
      )}
      onClick={onClick}
      style={
        {
          position: 'relative',
          '--slide-progress': active ? '1' : '0',
        } as React.CSSProperties
      }
    >
      <p
        className={cn(
          'text-sm font-semibold leading-[1] text-muted transition-all duration-200 ease-in-out sm:text-base',
          active && 'font-bold text-primary',
        )}
      >
        {label}
      </p>
      {/* the underline for the tab regardless if active or not */}
      <div
        className="absolute bottom-[-1.5px] left-0 h-[1.5px] w-full rounded-full bg-[#efefef] transition-transform duration-300 ease-out"
        style={{
          transformOrigin: 'left',
        }}
      />
      {/* the underline for the tab if active */}
      <div
        className="absolute bottom-[-1.5px] left-0 h-[1.5px] w-full rounded-full bg-primary transition-transform duration-300 ease-out"
        style={{
          transform: `scaleX(${active ? 1 : 0})`,
          transformOrigin: 'left',
        }}
      />
    </div>
  )
}

// ProfileActionButtons component extracted to be reused in footer
const ProfileActionButtons = ({
  user,
  isCurrentUser,
  handleSendReferralClick,
  navigate,
}: {
  user: PublicUserData
  isCurrentUser: boolean
  handleSendReferralClick: () => void
  navigate: (path: string) => void
}) => {
  return (
    <div className="flex w-full items-center justify-between px-4">
      {isCurrentUser ? (
        <VisibilityBadge />
      ) : (
        <div className="min-w-[130px]">
          <UserChatButton userId={user.id} />
        </div>
      )}
      {isCurrentUser ? (
        <Button
          onClick={() => navigate(paths.users.editProfile.getHref())}
          className="min-w-[150px] px-6"
          variant="default"
          size="default"
        >
          Edit Profile
        </Button>
      ) : (
        <SendReferralDialog
          trigger={
            <Button className="min-w-[150px] px-6" variant="default" size="default">
              Send Referral
            </Button>
          }
          receiverUsername={user.username}
          onBeforeOpen={handleSendReferralClick}
        />
      )}
    </div>
  )
}

const AppUserProfile = () => {
  const { username } = useParams<{ username: string }>()
  const { data: currentUser } = useMyUserData()
  const { data: otherUser } = useUserByUsername(username || '')
  const { data: licenses } = usePublicLicenses(otherUser?.id || 0)
  const eventStore = useEventStore()
  const [sessionId, setSessionId] = useState<string | undefined>(eventStore.sessionId || undefined)
  const [flowId, setFlowId] = useState<string | undefined>(eventStore.flowId || undefined)

  const [isBioModalOpen, setIsBioModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<ActiveTab>('reviews')

  const navigate = useNavigate()
  const isMobile = useIsMobile()

  const isCurrentUser = currentUser?.username === username
  const user = isCurrentUser ? currentUser : otherUser

  const { data: reviewData } = useUserReviews(user?.id || -1)
  const { data: publicUserLicenses } = usePublicLicenses(user?.id || -1)

  const setReceiver = useSendReferralStore((state) => state.setReceiver)
  const resetSendReferralStore = useSendReferralStore((state) => state.resetSendReferralStore)

  if (!user) {
    navigate('/404')
    return null
  }

  const hasApprovedLicense = licenses?.some((license: License) => license.status === 'approved')
  const isVerified = user.verified && hasApprovedLicense
  const reviewsCount = reviewData?.totalReviews ?? 0
  const averageRating = reviewData?.averageRating ?? 0
  const isLoanOfficer = user?.profession === 'mortgage_loan_officer'

  const handleSendReferralClick = () => {
    if (currentUser && currentUser.profileComplete !== 100) {
      return
    }
    resetSendReferralStore()
    setReceiver(user)

    if (!sessionId) {
      setSessionId(eventStore.setSessionId())
    }

    if (!flowId) {
      setFlowId(eventStore.setFlowId())
      sendEvent({
        userId: currentUser?.id,
        eventType: 'flow_start',
        info: {
          sessionId,
          flow_type: 'send_referral',
          flow_step: 'click_send_referral',
          flowId,
          receiver_id: user.id,
        },
      })
    } else {
      sendEvent({
        userId: currentUser?.id,
        eventType: 'flow_step',
        info: {
          sessionId,
          flow_type: 'send_referral',
          flow_step: 'click_send_referral',
          flowId,
          receiver_id: user.id,
        },
      })
    }
  }

  if (isMobile) {
    return (
      <div>
        <div className="flex min-h-screen flex-col">
          <header className="relative">
            <CoverPhoto userId={user.id} className="mt-3 h-[194px] w-full" />
            <div className="mb-[-25px] flex w-full translate-y-[-25px] flex-row justify-center rounded-[20px] bg-white text-primary">
              <div className="mb-[-50px] flex w-full translate-y-[-50px] flex-col items-center gap-2 px-5 md:px-8">
                <ProfileAvatar
                  userId={user.id}
                  size={88}
                  className="border-4 border-white shadow-[0_1px_6px_1px_rgba(27,48,41,0.10)]"
                  shouldZoom={true}
                />
                <div className="flex w-full flex-col items-center gap-[10px]">
                  <div className="flex flex-col items-center gap-[2px]">
                    <div className="flex items-center gap-2">
                      <h1 className={'text-[24px] font-bold md:text-[32px]'}>{user.fullName}</h1>
                      <VerificationBadge isVerified={isVerified || false} className="mt-0.5" size={24} />
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-primary md:text-[17px]">{professionMap[user.profession].label}</p>
                    </div>
                  </div>
                  {!user.biography ? (
                    <div className="relative w-full text-center text-sm italic text-muted md:text-[17px]">
                      <p className="line-clamp-1">{user.fullName} doesn&apos;t have a bio yet.</p>
                    </div>
                  ) : (
                    <TruncatedText
                      key={'mobile-bio'}
                      text={user.biography || ''}
                      numberOfLines={2}
                      customOverflowComponent={
                        <button
                          className="inline bg-white pl-1 text-primary underline"
                          onClick={() => setIsBioModalOpen(true)}
                        >
                          Read full bio
                        </button>
                      }
                      className="relative w-full max-w-[600px] text-center text-[13px] leading-[150%] md:text-[14px]"
                    />
                  )}
                  <OtherInfoContainer
                    isCurrentUser={false}
                    reviewsCount={reviewsCount}
                    averageRating={averageRating}
                    shortAddress={user.shortAddress || ''}
                    className="text-[12px] md:text-[14px]"
                  />
                </div>
              </div>
              <BioModal isOpen={isBioModalOpen} setOpen={setIsBioModalOpen} user={user} />
            </div>
          </header>
          <main className="my-7 flex flex-col gap-7 px-5 md:px-8">
            <div className="h-px w-full bg-[#efefef]" />
            <div className="flex flex-col gap-[26px] px-[6px]">
              <GeneralInfoSection
                type="mobile"
                icon={<VerifiedLicensesIcon className="size-7 text-primary md:size-8" />}
                headingClassName="mb-1"
                className="md:gap-4"
                title="Verified Licenses"
                subtitle={isLoanOfficer ? publicUserLicenses?.[0].licenseNumber || undefined : undefined}
                text={publicUserLicenses
                  ?.map((license) =>
                    isLoanOfficer
                      ? `${statesMap[license.state as unknown as State]?.label}`
                      : `${statesMap[license.state as unknown as State]?.label} (#${license.licenseNumber})`,
                  )
                  .join(` • `)}
              />
              {user?.companyName && (
                <GeneralInfoSection
                  type="mobile"
                  headingClassName="mb-1"
                  className="md:gap-4"
                  icon={<WorksAtIcon className="size-7 text-primary md:size-8" />}
                  title="Works at"
                  text={user?.companyName || 'N/A'}
                />
              )}
              {user?.expertise && user.expertise?.length > 0 && (
                <GeneralInfoSection
                  type="mobile"
                  headingClassName="mb-1"
                  className="md:gap-4"
                  icon={<ExpertiseIcon className="size-7 text-primary md:size-8" />}
                  title="Expertise"
                  text={user?.expertise
                    ?.map(
                      (e) => expertiseMap[user?.profession as unknown as Profession][e as unknown as Expertise]?.label,
                    )
                    .join(` • `)}
                />
              )}
              <GeneralInfoSection
                type="mobile"
                headingClassName="mb-1"
                className="md:gap-4"
                icon={<LanguagesIcon className="size-7 text-primary md:size-8" />}
                title="Languages"
                text={user?.languages?.map((e) => languageMap[e as unknown as Language]?.label).join(` • `)}
              />
            </div>
            <div className="flex flex-col">
              <div className="mb-[14px] flex flex-row items-start">
                {/* <Tab label="Listings" active={activeTab === 'listings'} onClick={() => setActiveTabAndURL('listings')} /> */}
                <Tab label="Reviews" active={activeTab === 'reviews'} onClick={() => setActiveTab('reviews')} />
                <Tab label="Portfolio" active={activeTab === 'portfolio'} onClick={() => setActiveTab('portfolio')} />
                <Tab label="Activity" active={activeTab === 'activity'} onClick={() => setActiveTab('activity')} />
              </div>
              {activeTab === 'listings' && (
                <ListingList showAll={true} firstName={user.firstName} username={username as string} />
              )}
              {activeTab === 'reviews' && (
                <ReviewList
                  showAll={true}
                  mobile
                  userId={user.id}
                  username={username as string}
                  firstName={user.firstName}
                  onlyShowReviewStatus={true}
                />
              )}
              {activeTab === 'portfolio' && (
                <PortfolioList
                  showAll={true}
                  mobile
                  userId={user.id}
                  username={username as string}
                  firstName={user.firstName}
                  profession={user.profession}
                />
              )}
              {activeTab === 'activity' && <UserFeedList mobile userId={user.id} firstName={user.firstName} />}
            </div>
          </main>
        </div>
        {/* Using the FixedFooter component */}
        <FixedFooter
          contentClassName="items-stretch py-2"
          className="z-50 border-t border-border shadow-lg"
          fixedHeight={false}
        >
          <ProfileActionButtons
            user={user}
            isCurrentUser={isCurrentUser}
            handleSendReferralClick={handleSendReferralClick}
            navigate={navigate}
          />
        </FixedFooter>
      </div>
    )
  }

  return (
    <div>
      <div className="flex min-h-screen flex-col">
        <header className="relative">
          <div className="relative">
            <CoverPhoto
              userId={user.id}
              className={`${isMobile ? 'mt-3 h-[194px]' : 'h-[200px]'} w-full rounded-b-[30px] border-b`}
            />
            <div className={cn('flex w-full flex-row', isMobile ? 'px-0' : 'px-10')}>
              <div
                className={cn(
                  'flex w-full translate-y-[-40px] flex-col gap-2  bg-white text-primary',
                  isMobile
                    ? 'rounded-[20px]'
                    : 'rounded-[40px] border-[0.5px] border-[rgba(170,171,173,0.40)] shadow-[0px_2px_12px_1px_rgba(27,48,41,0.10)]',
                )}
              >
                <div className="mb-[-20px] flex translate-y-[-60px] flex-col gap-2">
                  <div className="flex flex-col items-stretch gap-2 px-5 lg:px-8">
                    <ProfileAvatar
                      userId={user.id}
                      size={128}
                      className="self-center border-4 border-white shadow-[0_1px_6px_1px_rgba(27,48,41,0.10)]"
                      shouldZoom={true}
                    />
                    {/* )} */}
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col items-center gap-1">
                        <div className="flex items-center gap-2">
                          <h1 className={'text-[24px] font-bold lg:text-[32px]'}>{user.fullName}</h1>
                          <VerificationBadge isVerified={isVerified || false} className="mt-0.5" size={24} />
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-primary lg:text-[17px]">{professionMap[user.profession].label}</p>
                        </div>
                      </div>
                      {!user.biography ? (
                        <div className="relative w-full text-center text-sm italic text-muted lg:text-[17px]">
                          <p className="line-clamp-1">{user.fullName} doesn&apos;t have a bio yet.</p>
                        </div>
                      ) : (
                        <TruncatedText
                          key={'desktop-bio'}
                          text={user.biography || ''}
                          numberOfLines={2}
                          customOverflowComponent={
                            <button
                              className="inline bg-white pl-1 text-primary underline"
                              onClick={() => setIsBioModalOpen(true)}
                            >
                              Read full bio
                            </button>
                          }
                          className="relative mx-auto w-full max-w-[500px] text-center text-[17px] leading-[150%]"
                        />
                      )}
                      <OtherInfoContainer
                        isCurrentUser={false}
                        reviewsCount={reviewsCount}
                        averageRating={averageRating}
                        shortAddress={user.shortAddress || ''}
                        className="lg:text-[16px]"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <BioModal isOpen={isBioModalOpen} setOpen={setIsBioModalOpen} user={user} />
            </div>
          </div>
        </header>

        {isMobile && <div className="my-4 h-px w-full bg-[#efefef] px-10" />}

        <main className="mb-10 mt-[10px] flex flex-col gap-10 px-10">
          <div className="flex flex-col gap-[26px]">
            <GeneralInfoSection
              icon={<VerifiedLicensesIcon className="size-7 text-primary lg:size-8" />}
              headingClassName="mb-[6px]"
              title="Verified Licenses"
              subtitle={isLoanOfficer ? publicUserLicenses?.[0].licenseNumber || undefined : undefined}
              text={publicUserLicenses
                ?.map((license) =>
                  isLoanOfficer
                    ? `${statesMap[license.state as unknown as State]?.label}`
                    : `${statesMap[license.state as unknown as State]?.label} (#${license.licenseNumber})`,
                )
                .join(` • `)}
            />
            {user?.companyName && (
              <GeneralInfoSection
                headingClassName="mb-[6px]"
                icon={<WorksAtIcon className="size-7 text-primary md:size-8" />}
                title="Works at"
                text={user?.companyName || 'N/A'}
              />
            )}
            {user?.expertise && user.expertise?.length > 0 && (
              <GeneralInfoSection
                headingClassName="mb-[6px]"
                icon={<ExpertiseIcon className="size-7 text-primary md:size-8" />}
                title="Expertise"
                text={user?.expertise
                  ?.map(
                    (e) => expertiseMap[user?.profession as unknown as Profession][e as unknown as Expertise]?.label,
                  )
                  .join(` • `)}
              />
            )}
            <GeneralInfoSection
              headingClassName="mb-[6px]"
              icon={<LanguagesIcon className="size-7 text-primary md:size-8" />}
              title="Languages"
              text={user?.languages?.map((e) => languageMap[e as unknown as Language]?.label).join(` • `)}
            />
          </div>
          <div className="flex flex-col">
            <div className="mb-[30px] flex flex-row items-start">
              {/* <Tab label="Listings" active={activeTab === 'listings'} onClick={() => setActiveTabAndURL('listings')} /> */}
              <Tab label="Reviews" active={activeTab === 'reviews'} onClick={() => setActiveTab('reviews')} />
              <Tab label="Portfolio" active={activeTab === 'portfolio'} onClick={() => setActiveTab('portfolio')} />
              <Tab label="Activity" active={activeTab === 'activity'} onClick={() => setActiveTab('activity')} />
            </div>
            {activeTab === 'listings' && (
              <ListingList showAll={true} firstName={user.firstName} username={username as string} />
            )}
            {activeTab === 'reviews' && (
              <ReviewList
                showAll={true}
                mobile
                userId={user.id}
                username={username as string}
                firstName={user.firstName}
                onlyShowReviewStatus={true}
              />
            )}
            {activeTab === 'portfolio' && (
              <PortfolioList
                showAll={true}
                mobile
                userId={user.id}
                username={username as string}
                firstName={user.firstName}
                profession={user.profession}
              />
            )}
            {activeTab === 'activity' && <UserFeedList mobile userId={user.id} firstName={user.firstName} />}
          </div>
        </main>
      </div>

      {/* Using the FixedFooter component */}
      <FixedFooter
        contentClassName="items-stretch py-2"
        className="z-50 border-t border-border shadow-lg"
        fixedHeight={false}
      >
        <ProfileActionButtons
          user={user}
          isCurrentUser={isCurrentUser}
          handleSendReferralClick={handleSendReferralClick}
          navigate={navigate}
        />
      </FixedFooter>
    </div>
  )
}

export default AppUserProfile
